import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Stack,
  CardHeader,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  SportsCricket,
  EmojiEvents,
  Groups,
  DragIndicator,
  CompareArrows,
  DeleteOutline,
  Save,
  Warning,
  Refresh,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiService } from '../services/api';
import { useAutopilotPage } from '../autopilot/registry';
import {
  LineupEvaluation,
  TeamInfo,
  TeamRanking,
  LineupGenerationRequest,
  BatchLineupGenerationRequest,
  LeagueRankingsRequest,
  CheckSavedRankingsResponse,
} from '../types/lineup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lineup-tabpanel-${index}`}
      aria-labelledby={`lineup-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Draggable Lineup Card Component
interface DraggableLineupCardProps {
  lineup: LineupEvaluation;
  teamName: string;
  index: number;
  rank: number; // For ranking display
  teamId?: number; // Optional for batch mode
}

const DraggableLineupCard: React.FC<DraggableLineupCardProps> = ({ 
  lineup, 
  teamName,
  index,
  rank,
  teamId
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Use composite ID for batch mode to avoid conflicts between teams
  const sortableId = teamId ? `${teamId}-${index}` : lineup.lineup_num;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get rank color and medal
  const getRankBadge = (rank: number) => {
    if (rank === 1) return { color: 'gold', emoji: '🥇' };
    if (rank === 2) return { color: 'silver', emoji: '🥈' };
    if (rank === 3) return { color: '#CD7F32', emoji: '🥉' };
    return { color: 'grey', emoji: `#${rank}` };
  };

  const rankBadge = getRankBadge(rank);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        minWidth: 350,
        maxWidth: 400,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: isDragging ? 'grabbing' : 'pointer',
        border: isDragging ? '2px dashed #E74C91' : '1px solid #e0e0e0',
        boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: isDragging ? undefined : 'translateY(-4px)',
        },
      }}
    >
      {/* Drag Handle Header */}
      <CardHeader
        {...attributes}
        {...listeners}
        avatar={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicator sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }} />
            <Chip 
              label={rankBadge.emoji} 
              sx={{ 
                bgcolor: rankBadge.color,
                color: rank <= 3 ? 'white' : 'black',
                fontWeight: 'bold'
              }} 
              size="small" 
            />
          </Box>
        }
        title={
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {teamName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Lineup #{lineup.lineup_num}
            </Typography>
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip 
              label={`Bat 1st TV: ${getBattingFirstTV(lineup).toFixed(2)}`} 
              color="success" 
              size="small"
            />
            <Chip 
              label={`Bat 2nd TV: ${getBattingSecondTV(lineup).toFixed(2)}`} 
              color="info" 
              size="small"
            />
          </Box>
        }
        sx={{
          bgcolor: 'grey.50',
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      />

      <Divider />

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {/* Starting XI - Two Column Layout */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SportsCricket fontSize="small" color="primary" />
            Starting XI
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* First Column - 6 players */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(lineup.starting_xi || lineup.lineup_players || []).slice(0, 6).map((player: any, idx: number) => (
                <Chip 
                  key={idx} 
                  label={typeof player === 'object' ? player.player_name : player} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', justifyContent: 'flex-start' }}
                />
              ))}
            </Box>
            {/* Second Column - 5 players */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(lineup.starting_xi || lineup.lineup_players || []).slice(6, 11).map((player: any, idx: number) => (
                <Chip 
                  key={idx + 6} 
                  label={typeof player === 'object' ? player.player_name : player} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', justifyContent: 'flex-start' }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Impact Substitutes - Moved Above Metrics */}
        {lineup.sub_details && (lineup.sub_details.batter_name || lineup.sub_details.bowler_name) && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Impact Substitutes
              </Typography>
              <Stack spacing={1}>
                {lineup.sub_details.batter_name && (
                  <Box sx={{ 
                    bgcolor: 'rgba(25, 118, 210, 0.08)', 
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    p: 1, 
                    borderRadius: 1 
                  }}>
                    <Typography variant="caption" color="text.secondary">Batter</Typography>
                    <Typography variant="body2" fontWeight="bold">{lineup.sub_details.batter_name}</Typography>
                    <Typography variant="caption" color="text.secondary">TVAA: {lineup.sub_details.batter_tvaa?.toFixed(2)}</Typography>
                  </Box>
                )}
                {lineup.sub_details.bowler_name && (
                  <Box sx={{ 
                    bgcolor: 'rgba(220, 0, 78, 0.08)', 
                    border: '1px solid rgba(220, 0, 78, 0.2)',
                    p: 1, 
                    borderRadius: 1 
                  }}>
                    <Typography variant="caption" color="text.secondary">Bowler</Typography>
                    <Typography variant="body2" fontWeight="bold">{lineup.sub_details.bowler_name}</Typography>
                    <Typography variant="caption" color="text.secondary">TVAA: {lineup.sub_details.bowler_tvaa?.toFixed(2)}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Lineup Value Metrics - Summary */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Lineup Value Metrics
            </Typography>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? '▲' : '▼'}
              sx={{ fontSize: '0.7rem', minWidth: 'auto' }}
            >
              {expanded ? 'Less' : 'More'}
            </Button>
          </Box>
          
          {/* Always Visible - Key Metrics */}
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.light', p: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">Batting 1st TV:</Typography>
              <Typography variant="body2" fontWeight="bold">{getBattingFirstTV(lineup).toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'info.light', p: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">Batting 2nd TV:</Typography>
              <Typography variant="body2" fontWeight="bold">{getBattingSecondTV(lineup).toFixed(2)}</Typography>
            </Box>
          </Stack>

          {/* Expandable - Detailed Metrics */}
          {expanded && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'primary.main' }}>
                Batting Metrics
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Regular Batting TV:</Typography>
                  <Typography variant="caption" fontWeight="bold">{lineup.reg_bat_tv.toFixed(2)}</Typography>
                </Box>
                {lineup.imp_bat_tv && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Impact Batting TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{lineup.imp_bat_tv.toFixed(2)}</Typography>
                  </Box>
                )}
              </Stack>

              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'primary.main' }}>
                Bowling Metrics
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">5 Bowlers TV:</Typography>
                  <Typography variant="caption" fontWeight="bold">{lineup.five_bowl_tv.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">6 Bowlers TV:</Typography>
                  <Typography variant="caption" fontWeight="bold">{lineup.six_bowl_tv.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">7 Bowlers TV:</Typography>
                  <Typography variant="caption" fontWeight="bold">{lineup.seven_bowl_tv.toFixed(2)}</Typography>
                </Box>
                {lineup.six_bowl_tv_sub && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">6 Bowlers TV (Sub):</Typography>
                    <Typography variant="caption" fontWeight="bold">{lineup.six_bowl_tv_sub.toFixed(2)}</Typography>
                  </Box>
                )}
                {lineup.seven_bowl_tv_sub && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">7 Bowlers TV (Sub):</Typography>
                    <Typography variant="caption" fontWeight="bold">{lineup.seven_bowl_tv_sub.toFixed(2)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">NS Bowling TV:</Typography>
                  <Typography variant="caption" fontWeight="bold">{lineup.ns_bowl_tv.toFixed(2)}</Typography>
                </Box>
                {lineup.sub_bowl_tv && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Sub Bowling TV:</Typography>
                    <Typography variant="caption" fontWeight="bold">{lineup.sub_bowl_tv.toFixed(2)}</Typography>
                  </Box>
                )}
              </Stack>

              {/* Bowling Deployment Tables */}
              {(lineup['5_bowl_deployment'] || lineup['6_bowl_deployment'] || lineup['7_bowl_deployment']) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1.5, color: 'primary.main' }}>
                    Bowling Deployment (No Impact Sub)
                  </Typography>
                  
                  {/* 5 Bowler Deployment */}
                  {lineup['5_bowl_deployment'] && lineup['5_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        5 Bowlers
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['5_bowl_deployment'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* 6 Bowler Deployment */}
                  {lineup['6_bowl_deployment'] && lineup['6_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        6 Bowlers
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['6_bowl_deployment'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* 7 Bowler Deployment */}
                  {lineup['7_bowl_deployment'] && lineup['7_bowl_deployment'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        7 Bowlers
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['7_bowl_deployment'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}

              {/* Bowling Deployment with Impact Sub */}
              {(lineup['5_bowl_deployment_sub'] || lineup['6_bowl_deployment_sub'] || lineup['7_bowl_deployment_sub']) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1.5, color: 'secondary.main' }}>
                    Bowling Deployment (With Impact Sub)
                  </Typography>
                  
                  {/* 5 Bowler Deployment Sub */}
                  {lineup['5_bowl_deployment_sub'] && lineup['5_bowl_deployment_sub'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        5 Bowlers (Sub)
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['5_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* 6 Bowler Deployment Sub */}
                  {lineup['6_bowl_deployment_sub'] && lineup['6_bowl_deployment_sub'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        6 Bowlers (Sub)
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['6_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* 7 Bowler Deployment Sub */}
                  {lineup['7_bowl_deployment_sub'] && lineup['7_bowl_deployment_sub'].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        7 Bowlers (Sub)
                      </Typography>
                      <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Bowler</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>PP</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>MO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>SO</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lineup['7_bowl_deployment_sub'].map((deployment: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{deployment['Bowler Name']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Powerplay Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Middle Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem' }}>{deployment['Slog/Death Overs']}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{deployment['Total Overs']}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Footer with expand option */}
      <Divider />
      <Box sx={{ p: 1, bgcolor: 'grey.50', display: 'flex', justifyContent: 'center' }}>
        <Chip 
          icon={<CompareArrows />}
          label="Drag to Compare" 
          size="small" 
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>
    </Card>
  );
};

// Cache keys for localStorage
const CACHE_KEYS = {
  SINGLE_TEAM_LINEUPS: 'lineups_v1_single_team',
  BATCH_LINEUPS: 'lineups_v1_batch',
  RANKINGS: 'lineups_v1_rankings',
  TEAMS: 'lineups_v1_teams',
  SELECTED_TEAM: 'lineups_v1_selected_team',
  SELECTED_TEAMS: 'lineups_v1_selected_teams',
  CURRENT_TAB: 'lineups_v1_current_tab',
};

// Helper function to get batting first TV
const getBattingFirstTV = (lineup: LineupEvaluation): number => {
  return lineup.batting_first_tv;
};

// Helper function to get batting second TV
const getBattingSecondTV = (lineup: LineupEvaluation): number => {
  return lineup.batting_second_tv;
};

// Helper function to sort lineups by Average of Bat 1st TV and Bat 2nd TV (descending)
const sortLineupsByTV = (lineups: LineupEvaluation[]): LineupEvaluation[] => {
  return [...lineups].sort((a, b) => {
    // Calculate average of batting_first_tv and batting_second_tv
    const avgA = (a.batting_first_tv + a.batting_second_tv) / 2;
    const avgB = (b.batting_first_tv + b.batting_second_tv) / 2;
    return avgB - avgA; // Descending order (highest average first)
  });
};

// Migrate old lineup data to new field names
const migrateLineupData = (lineup: any): LineupEvaluation => {
  // If new fields exist, return as is
  if (lineup.batting_first_tv !== undefined && lineup.batting_second_tv !== undefined) {
    return lineup;
  }
  
  // Migrate from old fields to new fields
  return {
    ...lineup,
    batting_first_tv: lineup.total_tv_no_sub || (lineup.reg_bat_tv + lineup.ns_bowl_tv),
    batting_second_tv: lineup.total_tv_with_sub || lineup.total_tv_no_sub || (lineup.reg_bat_tv + lineup.ns_bowl_tv)
  };
};

// Load from cache with migration
const loadFromCache = <T,>(key: string, defaultValue: T): T => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return defaultValue;
    
    const parsed = JSON.parse(cached);
    
    // Migrate lineup data if needed
    if (key === CACHE_KEYS.SINGLE_TEAM_LINEUPS && Array.isArray(parsed)) {
      return parsed.map(migrateLineupData) as T;
    } else if (key === CACHE_KEYS.BATCH_LINEUPS && typeof parsed === 'object') {
      const migrated: any = {};
      Object.entries(parsed).forEach(([teamId, lineups]: [string, any]) => {
        migrated[teamId] = Array.isArray(lineups) ? lineups.map(migrateLineupData) : [];
      });
      return migrated as T;
    }
    
    return parsed;
  } catch (error) {
    console.error(`Error loading from cache (${key}):`, error);
    return defaultValue;
  }
};

// Save to cache
const saveToCache = <T,>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving to cache (${key}):`, error);
  }
};

// Progress Tracker Component
interface ProgressTrackerProps {
  loading: boolean;
  startTime: number | null;
  elapsedTime: number | null;
  message: string;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  loading,
  startTime,
  elapsedTime,
  message
}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (loading && startTime) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 100); // Update every 100ms for smooth display
      return () => clearInterval(interval);
    }
  }, [loading, startTime]);

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const runningTime = startTime ? (currentTime - startTime) / 1000 : 0;
  const displayTime = elapsedTime !== null ? elapsedTime : runningTime;

  if (!loading && elapsedTime === null) return null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
        <Typography variant="body2" color="primary" fontWeight="bold">
          {loading ? `${formatTime(runningTime)} elapsed` : `✓ Completed in ${formatTime(displayTime)}`}
        </Typography>
      </Box>
      <Box>
        <LinearProgress 
          variant={loading ? "indeterminate" : "determinate"}
          value={loading ? undefined : 100}
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: loading ? 'primary.main' : 'success.main'
            }
          }}
        />
      </Box>
      {!loading && elapsedTime !== null && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          Process completed successfully!
        </Typography>
      )}
    </Box>
  );
};

const LineupsV1: React.FC = () => {
  // Tab state with caching
  const [currentTab, setCurrentTab] = useState(() => 
    loadFromCache(CACHE_KEYS.CURRENT_TAB, 0)
  );

  // Teams data with caching
  const [teams, setTeams] = useState<TeamInfo[]>(() => 
    loadFromCache(CACHE_KEYS.TEAMS, [])
  );

  // Single team lineup generation with caching
  const [selectedTeam, setSelectedTeam] = useState<number>(() => 
    loadFromCache(CACHE_KEYS.SELECTED_TEAM, 3)
  );
  const [nLineups, setNLineups] = useState<number>(5);
  const [nationality, setNationality] = useState<string>('India');
  const [singleTeamLineups, setSingleTeamLineups] = useState<LineupEvaluation[]>(() => 
    loadFromCache(CACHE_KEYS.SINGLE_TEAM_LINEUPS, [])
  );
  const [loadingSingleTeam, setLoadingSingleTeam] = useState(false);
  const [singleTeamStartTime, setSingleTeamStartTime] = useState<number | null>(null);
  const [singleTeamElapsedTime, setSingleTeamElapsedTime] = useState<number | null>(null);

  // Batch lineup generation with caching
  const [selectedTeams, setSelectedTeams] = useState<number[]>(() => 
    loadFromCache(CACHE_KEYS.SELECTED_TEAMS, [1, 2, 3, 4, 5, 7, 8])
  );
  const [batchNLineups, setBatchNLineups] = useState<number>(3);
  const [batchLineups, setBatchLineups] = useState<{ [key: number]: LineupEvaluation[] }>(() => 
    loadFromCache(CACHE_KEYS.BATCH_LINEUPS, {})
  );
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [batchStartTime, setBatchStartTime] = useState<number | null>(null);
  const [batchElapsedTime, setBatchElapsedTime] = useState<number | null>(null);
  const [batchActiveTab, setBatchActiveTab] = useState<number>(0); // Active team tab in batch results

  // League rankings with caching
  const [rankings, setRankings] = useState<TeamRanking[]>(() => 
    loadFromCache(CACHE_KEYS.RANKINGS, [])
  );
  const [rankingsNLineups, setRankingsNLineups] = useState<number>(5);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [rankingsStartTime, setRankingsStartTime] = useState<number | null>(null);
  const [rankingsElapsedTime, setRankingsElapsedTime] = useState<number | null>(null);
  const [rankingsSeason, setRankingsSeason] = useState<number | null>(null);

  // Save rankings state
  const [savingRankings, setSavingRankings] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedRankingsInfo, setSavedRankingsInfo] = useState<CheckSavedRankingsResponse | null>(null);
  // Where the rankings on screen came from: the saved warehouse version (with its timestamp) or an
  // unsaved computation. Persisted so a reload does not silently replace unsaved work.
  const [rankingsSource, setRankingsSource] = useState<{ kind: 'saved' | 'computed'; savedAt?: string | null; season?: number | null } | null>(() =>
    loadFromCache(CACHE_KEYS.RANKINGS + '_source', null as { kind: 'saved' | 'computed'; savedAt?: string | null; season?: number | null } | null)
  );
  const [loadingSavedRankings, setLoadingSavedRankings] = useState(false);
  const [checkingSavedRankings, setCheckingSavedRankings] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load teams on mount - always fetch fresh teams
  useEffect(() => {
    loadTeams();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cache data whenever it changes
  useEffect(() => {
    saveToCache(CACHE_KEYS.CURRENT_TAB, currentTab);
  }, [currentTab]);

  useEffect(() => {
    if (teams.length > 0) {
      saveToCache(CACHE_KEYS.TEAMS, teams);
    }
  }, [teams]);

  useEffect(() => {
    saveToCache(CACHE_KEYS.SELECTED_TEAM, selectedTeam);
  }, [selectedTeam]);

  useEffect(() => {
    saveToCache(CACHE_KEYS.SELECTED_TEAMS, selectedTeams);
  }, [selectedTeams]);

  useEffect(() => {
    if (singleTeamLineups.length > 0) {
      saveToCache(CACHE_KEYS.SINGLE_TEAM_LINEUPS, singleTeamLineups);
    }
  }, [singleTeamLineups]);

  useEffect(() => {
    if (Object.keys(batchLineups).length > 0) {
      saveToCache(CACHE_KEYS.BATCH_LINEUPS, batchLineups);
    }
  }, [batchLineups]);

  useEffect(() => {
    if (rankings.length > 0) {
      saveToCache(CACHE_KEYS.RANKINGS, rankings);
    }
  }, [rankings]);

  const loadTeams = async () => {
    try {
      const response = await apiService.getTeamsV2(); // Using V2 API
      setTeams(response.teams);
    } catch (err) {
      console.error('Error loading teams:', err);
      // Don't show error to user on initial load - teams will load when backend is ready
    }
  };

  // Single Team Lineup Generation
  const generateSingleTeamLineups = async () => {
    const startTime = Date.now();
    try {
      setLoadingSingleTeam(true);
      setSingleTeamStartTime(startTime);
      setSingleTeamElapsedTime(null);
      setError(null);
      
      // Clear old lineups before generating new ones
      setSingleTeamLineups([]);
      localStorage.removeItem(CACHE_KEYS.SINGLE_TEAM_LINEUPS);

      const request: LineupGenerationRequest = {
        team_id: selectedTeam,
        n_lineups: nLineups,
        nationality: nationality,
      };

      const response = await apiService.generateLineupsV2(request, true); // Using V2 API with optimized=true
      
      // Sort lineups by Average TV (descending)
      const sortedLineups = sortLineupsByTV(response.lineups);
      setSingleTeamLineups(sortedLineups);
      
      // Complete - set elapsed time
      const elapsed = (Date.now() - startTime) / 1000;
      setSingleTeamElapsedTime(elapsed);
    } catch (err) {
      console.error('Error generating lineups:', err);
      setError('Failed to generate lineups');
      setSingleTeamElapsedTime(null);
    } finally {
      setLoadingSingleTeam(false);
    }
  };

  // Batch Lineup Generation
  const generateBatchLineups = async () => {
    const startTime = Date.now();
    try {
      setLoadingBatch(true);
      setBatchStartTime(startTime);
      setBatchElapsedTime(null);
      setError(null);
      
      // Clear old batch lineups before generating new ones
      setBatchLineups({});
      setBatchActiveTab(0); // Reset to first tab
      localStorage.removeItem(CACHE_KEYS.BATCH_LINEUPS);

      const request: BatchLineupGenerationRequest = {
        team_ids: selectedTeams,
        n_lineups: batchNLineups,
        nationality: nationality,
      };

      const response = await apiService.batchGenerateLineupsV2(request, true); // Using V2 API with optimized=true
      
      console.log('Batch response:', response);
      
      // Sort lineups for each team by Average TV (descending)
      const sortedBatchLineups: { [key: number]: LineupEvaluation[] } = {};
      Object.entries(response.teams).forEach(([teamId, lineups]) => {
        sortedBatchLineups[Number(teamId)] = sortLineupsByTV(lineups);
      });
      
      console.log('Sorted batch lineups:', sortedBatchLineups);
      setBatchLineups(sortedBatchLineups);
      
      // Complete - set elapsed time
      const elapsed = (Date.now() - startTime) / 1000;
      setBatchElapsedTime(elapsed);
    } catch (err) {
      console.error('Error generating batch lineups:', err);
      setError('Failed to generate batch lineups');
      setBatchElapsedTime(null);
    } finally {
      setLoadingBatch(false);
    }
  };

  // Check if saved rankings exist in BigQuery
  /** Load the latest saved league rankings from BigQuery (the warehouse version). */
  const loadSavedRankings = async (manual: boolean) => {
    try {
      setLoadingSavedRankings(true);
      const res = await apiService.getSavedRankings();
      setRankings(res.rankings);
      setRankingsSeason(res.season);
      const src = { kind: 'saved' as const, savedAt: res.saved_at ?? null, season: res.season };
      setRankingsSource(src);
      saveToCache(CACHE_KEYS.RANKINGS + '_source', src);
      setError(null);
    } catch (err: any) {
      console.warn('No saved rankings loaded:', err);
      if (manual) setError(err?.message || 'No saved rankings found in BigQuery');
    } finally {
      setLoadingSavedRankings(false);
    }
  };

  const checkSavedRankings = async () => {
    try {
      setCheckingSavedRankings(true);
      const info = await apiService.checkSavedRankings();
      setSavedRankingsInfo(info);
    } catch (err) {
      console.error('Error checking saved rankings:', err);
      setSavedRankingsInfo(null);
    } finally {
      setCheckingSavedRankings(false);
    }
  };

  // League Rankings Generation
  const generateLeagueRankings = async () => {
    const startTime = Date.now();
    try {
      setLoadingRankings(true);
      setRankingsStartTime(startTime);
      setRankingsElapsedTime(null);
      setError(null);

      const request: LeagueRankingsRequest = {
        team_ids: selectedTeams,
        n_lineups: rankingsNLineups,
        nationality: nationality,
      };

      const response = await apiService.getLeagueRankingsV2(request.n_lineups, request.nationality, true); // Using V2 API with optimized=true
      
      setRankings(response.rankings);
      setRankingsSeason(response.season);
      const computedSrc = { kind: 'computed' as const, season: response.season };
      setRankingsSource(computedSrc);
      saveToCache(CACHE_KEYS.RANKINGS + '_source', computedSrc);
      
      // Check if saved rankings exist
      checkSavedRankings();
      
      // Complete - set elapsed time
      const elapsed = (Date.now() - startTime) / 1000;
      setRankingsElapsedTime(elapsed);
    } catch (err) {
      console.error('Error generating rankings:', err);
      setError('Failed to generate league rankings');
      setRankingsElapsedTime(null);
    } finally {
      setLoadingRankings(false);
    }
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (rankings.length === 0) {
      alert('No rankings to save. Please generate rankings first.');
      return;
    }

    // Check if saved rankings exist
    await checkSavedRankings();
    
    // If saved rankings exist, show confirmation dialog
    if (savedRankingsInfo?.table_exists) {
      setSaveDialogOpen(true);
    } else {
      // No existing rankings, save directly
      await saveRankings(false);
    }
  };

  // Save rankings to BigQuery
  const saveRankings = async (overwrite: boolean) => {
    if (!rankingsSeason) {
      alert('Season information not available. Please regenerate rankings.');
      return;
    }

    try {
      setSavingRankings(true);
      setError(null);
      
      const response = await apiService.saveLeagueRankings(
        rankings,
        rankingsSeason,
        overwrite
      );
      
      if (response.success) {
        alert(`✅ ${response.message}\n\nSaved ${response.rows_saved} rankings to ${response.table_name}`);
        // Refresh saved rankings info; what is on screen is now the saved version
        await checkSavedRankings();
        const savedSrc = { kind: 'saved' as const, savedAt: new Date().toISOString(), season: rankingsSeason };
        setRankingsSource(savedSrc);
        saveToCache(CACHE_KEYS.RANKINGS + '_source', savedSrc);
      } else {
        alert(`❌ Failed to save rankings: ${response.message}`);
      }
    } catch (err: any) {
      console.error('Error saving rankings:', err);
      const errorMessage = err?.response?.data?.detail?.message || err?.message || 'Failed to save rankings';
      setError(errorMessage);
      
      // Check if it's an overwrite error
      if (err?.response?.data?.detail?.table_exists) {
        setSaveDialogOpen(true);
      } else {
        alert(`❌ Error saving rankings: ${errorMessage}`);
      }
    } finally {
      setSavingRankings(false);
      setSaveDialogOpen(false);
    }
  };

  // Load saved rankings info on mount (if rankings exist)
  useEffect(() => {
    checkSavedRankings();
    // Fetch the saved warehouse version on open. Unsaved computed rankings are kept so they are not lost.
    if (!(rankings.length > 0 && rankingsSource?.kind === 'computed')) {
      loadSavedRankings(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getTeamName = (teamId: number): string => {
    return teams.find(t => t.team_id === teamId)?.team_name || `Team ${teamId}`;
  };

  // ---------------------------------------------------------------------------
  // Auto-pilot registration (PILOT): while this page is mounted, the assistant's
  // Auto-pilot mode can read its state and drive it. Handlers close over fresh
  // state because registration is refreshed every render.
  // ---------------------------------------------------------------------------
  const avgTV = (l: LineupEvaluation) =>
    Math.round(((l.batting_first_tv + l.batting_second_tv) / 2) * 1000) / 1000;

  useAutopilotPage({
    page: 'lineups',
    describeState: () => ({
      active_tab: currentTab === 0 ? 'single' : currentTab === 1 ? 'batch' : 'rankings',
      nationality,
      selected_team_id: selectedTeam,
      selected_team_name: getTeamName(selectedTeam),
      n_lineups: nLineups,
      teams: teams.map((t) => ({ team_id: t.team_id, team_name: t.team_name })),
      single_results: singleTeamLineups.length
        ? { count: singleTeamLineups.length, best_avg_tv: avgTV(singleTeamLineups[0]) }
        : null,
      rankings: rankings.length
        ? { count: rankings.length, season: rankingsSeason, top_team: rankings[0]?.team }
        : null,
      rankings_saved_in_bigquery: savedRankingsInfo?.table_exists ?? null,
    }),
    actions: {
      switch_tab: ({ tab }: { tab: string }) => {
        setCurrentTab(tab === 'single' ? 0 : tab === 'batch' ? 1 : 2);
      },
      select_team: ({ team_id }: { team_id: number }) => {
        setSelectedTeam(Number(team_id));
      },
      set_n_lineups: ({ n }: { n: number }) => {
        setNLineups(Math.max(1, Math.min(16, Number(n) || 5)));
      },
      set_nationality: ({ nationality: nat }: { nationality: string }) => {
        setNationality(String(nat || 'India'));
      },
      // The agent computed lineups server-side; show them exactly as if the
      // user had generated them here.
      apply_generated_lineups: (args: {
        team_id?: number;
        n_lineups?: number;
        nationality?: string;
        lineups?: LineupEvaluation[];
      }) => {
        setCurrentTab(0);
        if (args.team_id != null) setSelectedTeam(Number(args.team_id));
        if (args.n_lineups != null) setNLineups(Number(args.n_lineups));
        if (args.nationality) setNationality(args.nationality);
        setSingleTeamLineups(sortLineupsByTV(args.lineups || []));
        setSingleTeamElapsedTime(null);
        setError(null);
      },
      apply_rankings: (args: { season?: number | null; rankings?: TeamRanking[] }) => {
        setCurrentTab(2);
        setRankings(args.rankings || []);
        setRankingsSeason(args.season ?? null);
        checkSavedRankings();
      },
      // Confirm-gated: only ever invoked after the user clicks Confirm in chat.
      save_rankings: async () => {
        if (rankings.length === 0) {
          throw new Error('There are no rankings on the page to save — generate them first.');
        }
        await saveRankings(true);
        return '💾 Save finished — the page notification shows the result.';
      },
    },
  });

  const getRankColor = (rank: number): string => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return '#CD7F32'; // bronze
    return 'grey';
  };

  // Handle drag end for single team lineups
  const handleSingleTeamDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = singleTeamLineups.findIndex(lineup => lineup.lineup_num === active.id);
      const newIndex = singleTeamLineups.findIndex(lineup => lineup.lineup_num === over.id);
      
      setSingleTeamLineups(arrayMove(singleTeamLineups, oldIndex, newIndex));
    }
  };

  // Handle drag end for batch lineups (specific team)
  const handleBatchDragEnd = (teamId: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const teamLineups = batchLineups[teamId];
      const oldIndex = teamLineups.findIndex(lineup => lineup.lineup_num === active.id);
      const newIndex = teamLineups.findIndex(lineup => lineup.lineup_num === over.id);
      
      setBatchLineups({
        ...batchLineups,
        [teamId]: arrayMove(teamLineups, oldIndex, newIndex)
      });
    }
  };


  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsCricket color="primary" />
          Team Lineups & Rankings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate optimized lineups for single teams, multiple teams, or view league-wide rankings
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Single Team" icon={<SportsCricket />} iconPosition="start" />
          <Tab label="Batch Generation" icon={<Groups />} iconPosition="start" />
          <Tab label="League Rankings" icon={<EmojiEvents />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab 1: Single Team Lineup Generation */}
      <TabPanel value={currentTab} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generate Lineups for a Team
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ flex: '1 1 200px', minWidth: 200 }} size="small">
                  <InputLabel>Select Team</InputLabel>
                  <Select
                    value={teams.length > 0 ? selectedTeam : ''}
                    onChange={(e) => setSelectedTeam(Number(e.target.value))}
                    label="Select Team"
                    disabled={teams.length === 0}
                  >
                    {teams.length === 0 ? (
                      <MenuItem value="" disabled>
                        Loading teams...
                      </MenuItem>
                    ) : (
                      teams.map((team) => (
                        <MenuItem key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <TextField
                  label="Number of Lineups"
                  type="number"
                  value={nLineups}
                  onChange={(e) => setNLineups(Math.max(1, Math.min(16, parseInt(e.target.value) || 1)))}
                  inputProps={{ min: 1, max: 16 }}
                  size="small"
                  sx={{ flex: '1 1 150px', minWidth: 150 }}
                />
                <FormControl sx={{ flex: '1 1 150px', minWidth: 150 }} size="small">
                  <InputLabel>Nationality</InputLabel>
                  <Select
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    label="Nationality"
                  >
                    <MenuItem value="India">India</MenuItem>
                    <MenuItem value="West Indies">West Indies</MenuItem>
              
                    <MenuItem value="South Africa">South Africa</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={generateSingleTeamLineups}
                  disabled={loadingSingleTeam}
                  startIcon={loadingSingleTeam ? <CircularProgress size={20} /> : <SportsCricket />}
                  sx={{ flex: '0 0 auto' }}
                >
                  Generate
                </Button>
              </Box>
            </Stack>
            <ProgressTracker
              loading={loadingSingleTeam}
              startTime={singleTeamStartTime}
              elapsedTime={singleTeamElapsedTime}
              message={`Generating ${nLineups} lineups for ${getTeamName(selectedTeam)}...`}
            />
          </CardContent>
        </Card>

        {/* Single Team Results */}
        {singleTeamLineups.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                {getTeamName(selectedTeam)} - {singleTeamLineups.length} Lineups Generated
              </Typography>
              <Chip 
                icon={<DragIndicator />}
                label="Drag cards to reorder and compare" 
                color="info" 
                variant="outlined"
              />
            </Box>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSingleTeamDragEnd}
            >
              <SortableContext
                items={singleTeamLineups.map(lineup => lineup.lineup_num)}
                strategy={horizontalListSortingStrategy}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    overflowX: 'auto',
                    pb: 2,
                    '&::-webkit-scrollbar': {
                      height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'grey.100',
                      borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'primary.main',
                      borderRadius: 4,
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                >
                  {singleTeamLineups.map((lineup, index) => (
                    <DraggableLineupCard
                      key={lineup.lineup_num}
                      lineup={lineup}
                      teamName={getTeamName(selectedTeam)}
                      index={index}
                      rank={index + 1}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          </Box>
        )}

        {!loadingSingleTeam && singleTeamLineups.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SportsCricket sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No lineups generated yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a team and click Generate to create optimized lineups
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Tab 2: Batch Lineup Generation */}
      <TabPanel value={currentTab} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generate Lineups for Multiple Teams
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Teams</InputLabel>
                  <Select
                    multiple
                    value={teams.length > 0 ? selectedTeams : []}
                    onChange={(e) => setSelectedTeams(e.target.value as number[])}
                    label="Select Teams"
                    disabled={teams.length === 0}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {teams.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Loading teams...</Typography>
                        ) : (
                          (selected as number[]).map((teamId) => (
                            <Chip key={teamId} label={getTeamName(teamId)} size="small" />
                          ))
                        )}
                      </Box>
                    )}
                  >
                    {teams.length === 0 ? (
                      <MenuItem value="" disabled>
                        Loading teams...
                      </MenuItem>
                    ) : (
                      teams.map((team) => (
                        <MenuItem key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedTeams(teams.map(t => t.team_id))}
                    disabled={teams.length === 0 || selectedTeams.length === teams.length}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedTeams([])}
                    disabled={teams.length === 0 || selectedTeams.length === 0}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
              
              <TextField
                label="Lineups per Team"
                type="number"
                value={batchNLineups}
                onChange={(e) => setBatchNLineups(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 10 }}
                size="small"
                sx={{ flex: '1 1 150px', minWidth: 150 }}
              />
              <Button
                variant="contained"
                onClick={generateBatchLineups}
                disabled={loadingBatch || selectedTeams.length === 0}
                startIcon={loadingBatch ? <CircularProgress size={20} /> : <Groups />}
                sx={{ flex: '0 0 auto' }}
              >
                Generate All
              </Button>
            </Box>
            <ProgressTracker
              loading={loadingBatch}
              startTime={batchStartTime}
              elapsedTime={batchElapsedTime}
              message={`Generating ${batchNLineups} lineups for ${selectedTeams.length} teams...`}
            />
          </CardContent>
        </Card>

        {/* Batch Results - Tabbed Interface */}
        {Object.keys(batchLineups).length > 0 && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Groups color="primary" />
              Batch Generation Results ({Object.keys(batchLineups).length} teams)
            </Typography>

            {/* Team Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={batchActiveTab} 
                onChange={(_, newValue) => setBatchActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {Object.entries(batchLineups).map(([teamId, lineups], index) => (
                  <Tab 
                    key={teamId}
                    label={getTeamName(Number(teamId))}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Tab Content - Each Team's Lineups */}
            {Object.entries(batchLineups).map(([teamId, lineups], index) => (
              <TabPanel key={teamId} value={batchActiveTab} index={index}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {getTeamName(Number(teamId))} - {lineups.length} Lineups
                </Typography>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleBatchDragEnd(Number(teamId))}
                >
                  <SortableContext
                    items={lineups.map((lineup, idx) => `${teamId}-${idx}`)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 3,
                        overflowX: 'auto',
                        pb: 2,
                        '&::-webkit-scrollbar': {
                          height: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'grey.100',
                          borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'primary.main',
                          borderRadius: 4,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      {lineups.map((lineup, lineupIndex) => (
                        <DraggableLineupCard
                          key={`${teamId}-${lineupIndex}`}
                          lineup={lineup}
                          teamName={getTeamName(Number(teamId))}
                          index={lineupIndex}
                          rank={lineupIndex + 1}
                          teamId={Number(teamId)}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              </TabPanel>
            ))}
          </Box>
        )}

        {!loadingBatch && Object.keys(batchLineups).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Groups sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No batch lineups generated yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select multiple teams and click Generate All
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Tab 3: League Rankings */}
      <TabPanel value={currentTab} index={2}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generate League-Wide Team Rankings
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Teams</InputLabel>
                  <Select
                    multiple
                    value={teams.length > 0 ? selectedTeams : []}
                    onChange={(e) => setSelectedTeams(e.target.value as number[])}
                    label="Select Teams"
                    disabled={teams.length === 0}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {teams.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Loading teams...</Typography>
                        ) : (
                          (selected as number[]).map((teamId) => (
                            <Chip key={teamId} label={getTeamName(teamId)} size="small" />
                          ))
                        )}
                      </Box>
                    )}
                  >
                    {teams.length === 0 ? (
                      <MenuItem value="" disabled>
                        Loading teams...
                      </MenuItem>
                    ) : (
                      teams.map((team) => (
                        <MenuItem key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedTeams(teams.map(t => t.team_id))}
                    disabled={teams.length === 0 || selectedTeams.length === teams.length}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedTeams([])}
                    disabled={teams.length === 0 || selectedTeams.length === 0}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
              <TextField
                label="Lineups per Team"
                type="number"
                value={rankingsNLineups}
                onChange={(e) => setRankingsNLineups(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 10 }}
                size="small"
                sx={{ flex: '1 1 150px', minWidth: 150 }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  // Clear cached rankings before generating new ones
                  setRankings([]);
                  localStorage.removeItem(CACHE_KEYS.RANKINGS);
                  generateLeagueRankings();
                }}
                disabled={loadingRankings || selectedTeams.length === 0}
                startIcon={loadingRankings ? <CircularProgress size={20} /> : <EmojiEvents />}
                sx={{ flex: '0 0 auto' }}
              >
                {rankings.length > 0 ? 'Regenerate Rankings' : 'Calculate Rankings'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={async () => {
                  try {
                    // V2 doesn't cache rankings - just clear local storage
                    setRankings([]);
                    localStorage.removeItem(CACHE_KEYS.RANKINGS);
                    alert('Local cache cleared! V2 API always calculates fresh rankings.');
                  } catch (error) {
                    console.error('Error clearing cache:', error);
                    alert('Failed to clear cache. Please try again.');
                  }
                }}
                startIcon={<DeleteOutline />}
                sx={{ flex: '0 0 auto' }}
              >
                Clear Output
              </Button>
            </Box>
            <ProgressTracker
              loading={loadingRankings}
              startTime={rankingsStartTime}
              elapsedTime={rankingsElapsedTime}
              message={`Calculating rankings for ${selectedTeams.length} teams with ${rankingsNLineups} lineups each...`}
            />
          </CardContent>
        </Card>

        {/* Rankings Table */}
        {rankings.length > 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmojiEvents color="primary" />
                  League Rankings
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {rankingsSource && (
                    <Typography variant="caption" color="text.secondary">
                      {rankingsSource.kind === 'saved'
                        ? `Saved version${rankingsSource.savedAt ? ' · ' + new Date(rankingsSource.savedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}${rankingsSource.season ? ' · season ' + rankingsSource.season : ''}`
                        : 'Computed now · not saved yet'}
                    </Typography>
                  )}
                  <Tooltip title="Reload the latest saved rankings from BigQuery">
                    <span>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={loadingSavedRankings ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                        onClick={() => loadSavedRankings(true)}
                        disabled={loadingSavedRankings || loadingRankings}
                      >
                        Refresh from saved
                      </Button>
                    </span>
                  </Tooltip>
                  {savedRankingsInfo?.table_exists && (
                    <Tooltip title={`Saved rankings exist for season ${savedRankingsInfo.season} (${savedRankingsInfo.num_teams} teams)`}>
                      <Chip 
                        icon={<Save />}
                        label="Saved in BigQuery"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={savingRankings ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleSaveClick}
                    disabled={savingRankings || checkingSavedRankings}
                    size="small"
                  >
                    {savingRankings ? 'Saving...' : 'Save Output'}
                  </Button>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                This is the league rankings table the Trade Machine and Trade Scorecard read from. Overall Bat TV = (Reg Bat + Imp Bat) / 2, Overall Bowl TV = (Sub Bowl + NS Bowl) / 2, Overall Team TV = Overall Bat TV + 1.49 × Overall Bowl TV, which sets the rank.
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    {/* Group Header Row */}
                    <TableRow>
                      <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.04)', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}>Rank</TableCell>
                      <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.04)', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}>Team</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76, 175, 80, 0.08)', color: 'success.dark', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}>Batting First Metrics</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(33, 150, 243, 0.08)', color: 'info.dark', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}>Batting Second Metrics</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.08)', color: 'primary.main', borderRight: '2px solid', borderColor: 'divider', fontSize: '0.875rem' }}><Tooltip title="Average of the two scenarios: Bat TV = (Reg Bat + Imp Bat) / 2, Bowl TV = (Sub Bowl + NS Bowl) / 2"><span>Overall TV</span></Tooltip></TableCell>
                      <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold', bgcolor: 'rgba(156, 39, 176, 0.08)', color: 'primary.main', fontSize: '0.875rem' }}><Tooltip title="Overall Bat TV + 1.49 × Overall Bowl TV. Ranking basis."><span>Overall Team TV</span></Tooltip></TableCell>
                    </TableRow>
                    {/* Column Header Row */}
                    <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(76, 175, 80, 0.04)' }}><Tooltip title="Regular Batting Trade Value"><span>Reg Bat TV</span></Tooltip></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(76, 175, 80, 0.04)', borderRight: '2px solid', borderColor: 'divider' }}><Tooltip title="Substitution Bowling TV"><span>Sub Bowl TV</span></Tooltip></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(33, 150, 243, 0.04)' }}><Tooltip title="Impact Batting Trade Value"><span>Imp Bat TV</span></Tooltip></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(33, 150, 243, 0.04)', borderRight: '2px solid', borderColor: 'divider' }}><Tooltip title="No-Substitution Bowling TV"><span>NS Bowl TV</span></Tooltip></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(156, 39, 176, 0.04)' }}><Tooltip title="Overall Bat TV = (Reg Bat TV + Imp Bat TV) / 2"><span>Bat TV</span></Tooltip></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', bgcolor: 'rgba(156, 39, 176, 0.04)', borderRight: '2px solid', borderColor: 'divider' }}><Tooltip title="Overall Bowl TV = (Sub Bowl TV + NS Bowl TV) / 2"><span>Bowl TV</span></Tooltip></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rankings.map((ranking) => (
                      <TableRow 
                        key={ranking.team_id}
                        sx={{ 
                          bgcolor: ranking.rank <= 2 ? `${getRankColor(ranking.rank)}15` : 'inherit'
                        }}
                      >
                        <TableCell align="center" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {ranking.rank <= 3 && (
                              <EmojiEvents sx={{ color: getRankColor(ranking.rank), fontSize: '1.2rem' }} />
                            )}
                            <Typography fontWeight="bold">{ranking.rank}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: '2px solid', borderColor: 'divider' }}>
                          <Typography fontWeight="bold">{ranking.team}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(76, 175, 80, 0.02)' }}>{ranking.reg_bat_tv.toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(76, 175, 80, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{ranking.sub_bowl_tv.toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(33, 150, 243, 0.02)' }}>{ranking.imp_bat_tv.toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(33, 150, 243, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{ranking.ns_bowl_tv.toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)' }}>{(ranking.overall_bat_tv ?? (ranking.reg_bat_tv + ranking.imp_bat_tv) / 2).toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)', borderRight: '2px solid', borderColor: 'divider' }}>{(ranking.overall_bowl_tv ?? (ranking.sub_bowl_tv + ranking.ns_bowl_tv) / 2).toFixed(2)}</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'rgba(156, 39, 176, 0.02)' }}>
                          <Chip label={(ranking.overall_team_tv ?? ranking.team_tv ?? 0).toFixed(2)} color="secondary" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {!loadingRankings && rankings.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <EmojiEvents sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No rankings calculated yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select teams and click Calculate Rankings to see league standings
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Save Rankings Confirmation Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        aria-labelledby="save-rankings-dialog-title"
      >
        <DialogTitle id="save-rankings-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Save a New Version of the Rankings?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Saved league rankings already exist in BigQuery:
            <br />
            <strong>Season:</strong> {savedRankingsInfo?.season}
            <br />
            <strong>Teams:</strong> {savedRankingsInfo?.num_teams}
            <br />
            <strong>Saved At:</strong> {savedRankingsInfo?.saved_at ? new Date(savedRankingsInfo.saved_at).toLocaleString() : 'N/A'}
            <br />
            <br />
            Saved rankings already exist. Saving again adds a <strong>new version</strong>; earlier versions are kept and stay available in the Trade Scorecard.
            <br />
            <br />
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => saveRankings(true)} 
            color="warning" 
            variant="contained"
            disabled={savingRankings}
            startIcon={savingRankings ? <CircularProgress size={20} /> : <Save />}
          >
            {savingRankings ? 'Saving...' : 'Yes, Save New Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LineupsV1;

